import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea, UnstyledButton } from '@/shared/ui';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from '@/shared/ui/components/shadcn/dialog';
import { DialogPrimitive } from '@/shared/ui';

interface CancelDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  reason: string;
  onReasonChange: (v: string) => void;
  onConfirm: () => void;
}

/**
 * Confirmation dialog for cancelling a visit with an optional reason field.
 */
export function CancelDialog({ open, onOpenChange, reason, onReasonChange, onConfirm }: CancelDialogProps) {
  const { t } = useTranslation('@pages/agencies');
  const canConfirm = reason.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[10001]" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[10002] w-full max-w-[420px] translate-x-[-50%] translate-y-[-50%] border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-lg)] rounded-(--radius-lg) p-[var(--space-6)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <div className="mb-[var(--space-4)]">
            <div className="text-[var(--text-app-section)] font-[var(--font-bold)] text-[var(--text-primary)] mb-[var(--space-1)]">
              {t('visits.cancel.title', 'Cancelar visita')}
            </div>
            <div className="text-[var(--text-body-sm)] text-[var(--text-secondary)]">
              {t('visits.cancel.subtitle', 'Informe o motivo do cancelamento')}
            </div>
          </div>

          <div className="mb-[var(--space-5)]">
            <div className="text-[var(--text-meta)] font-[var(--font-semibold)] text-[var(--text-secondary)] mb-[var(--space-2)]">
              {t('visits.cancel.reasonLabel', 'Motivo')}
            </div>
            <Textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder={t('visits.cancel.placeholder', 'Ex: Cliente solicitou reagendamento...')}
              rows={3}
              autoFocus
              className="w-full px-[var(--space-3)] py-[var(--space-2)] border border-[var(--border-subtle)] rounded-(--radius-md) bg-[var(--surface-secondary)] text-[var(--text-primary)] text-[var(--text-body-sm)] font-[inherit] outline-none resize-y"
            />
          </div>

          <div className="flex justify-end gap-[var(--space-2)]">
            <Button
              variant="none"
              disableSize
              onClick={() => onOpenChange(false)}
              className="px-[var(--space-4)] py-[var(--space-2)] text-[var(--text-body-sm)] font-[var(--font-medium)] border border-[var(--border-subtle)] rounded-(--radius-md) bg-[var(--surface-secondary)] text-[var(--text-secondary)] cursor-pointer"
            >
              {t('visits.cancel.back', 'Voltar')}
            </Button>
            <Button
              variant="none"
              disableSize
              onClick={onConfirm}
              disabled={!canConfirm}
              className={`px-[var(--space-4)] py-[var(--space-2)] text-[var(--text-body-sm)] font-[var(--font-semibold)] border-none rounded-(--radius-md) ${canConfirm ? 'bg-[var(--status-error)] text-[var(--text-inverse)] cursor-pointer' : 'bg-[var(--surface-tertiary)] text-[var(--text-tertiary)] cursor-not-allowed'}`}
            >
              {t('visits.cancel.confirm', 'Confirmar cancelamento')}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  rating: number;
  onRatingChange: (v: number) => void;
  obs: string;
  onObsChange: (v: string) => void;
  onConfirm: () => void;
}

/**
 * Post-visit feedback dialog with star rating and optional observation text.
 */
export function FeedbackDialog({ open, onOpenChange, rating, onRatingChange, obs, onObsChange, onConfirm }: FeedbackDialogProps) {
  const { t } = useTranslation('@pages/agencies');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[10001]" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[10002] w-full max-w-[420px] translate-x-[-50%] translate-y-[-50%] border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-lg)] rounded-(--radius-lg) p-[var(--space-6)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <div className="mb-[var(--space-4)]">
            <div className="text-[var(--text-app-section)] font-[var(--font-bold)] text-[var(--text-primary)] mb-[var(--space-1)]">
              {t('visits.feedback.title', 'Feedback da visita')}
            </div>
            <div className="text-[var(--text-body-sm)] text-[var(--text-secondary)]">
              {t('visits.feedback.subtitle', 'Como foi a visita?')}
            </div>
          </div>

          <div className="mb-[var(--space-4)]">
            <div className="text-[var(--text-meta)] font-[var(--font-semibold)] text-[var(--text-secondary)] mb-[var(--space-2)]">
              {t('visits.feedback.rating', 'Avaliação')}
            </div>
            <div className="flex gap-[var(--space-1)]">
              {[1, 2, 3, 4, 5].map((star) => (
                <UnstyledButton
                  key={star}
                  type="button"
                  onClick={() => onRatingChange(star)}
                  className="cursor-pointer p-(--space-0-5) text-[length:var(--text-xl)] leading-none"
                  style={{ color: star <= rating ? 'var(--status-warning)' : 'var(--border-subtle)' }}
                >
                  ★
                </UnstyledButton>
              ))}
            </div>
          </div>

          <div className="mb-[var(--space-5)]">
            <div className="text-[var(--text-meta)] font-[var(--font-semibold)] text-[var(--text-secondary)] mb-[var(--space-2)]">
              {t('visits.feedback.brokerNotes', 'Observações')}
            </div>
            <Textarea
              value={obs}
              onChange={(e) => onObsChange(e.target.value)}
              placeholder={t('visits.feedback.placeholder', 'Anotações sobre a visita...')}
              rows={3}
              className="w-full px-[var(--space-3)] py-[var(--space-2)] border border-[var(--border-subtle)] rounded-(--radius-md) bg-[var(--surface-secondary)] text-[var(--text-primary)] text-[var(--text-body-sm)] font-[inherit] outline-none resize-y"
            />
          </div>

          <div className="flex justify-end gap-[var(--space-2)]">
            <Button
              variant="none"
              disableSize
              onClick={() => onOpenChange(false)}
              className="px-[var(--space-4)] py-[var(--space-2)] text-[var(--text-body-sm)] font-[var(--font-medium)] border border-[var(--border-subtle)] rounded-(--radius-md) bg-[var(--surface-secondary)] text-[var(--text-secondary)] cursor-pointer"
            >
              {t('visits.form.conflict.cancel', 'Cancelar')}
            </Button>
            <Button
              variant="none"
              disableSize
              onClick={onConfirm}
              className="px-[var(--space-4)] py-[var(--space-2)] text-[var(--text-body-sm)] font-[var(--font-semibold)] border-none rounded-(--radius-md) bg-[var(--seed-base)] text-[var(--text-inverse)] cursor-pointer"
            >
              {t('visits.feedback.confirm', 'Confirmar')}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
